import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';
import AccessAndPricingFAQ from '../../components/faq/AccessAndPricingFAQ';
import { serviceRoutesById } from '../../navigation/publicServiceRoutes';

const catalogPath = '/app/catalog';

const accessModelCards = [
  {
    id: 'perceptual-skill-development',
    icon: 'listen',
    title: serviceRoutesById['perceptual-skill-development'].label,
    model: 'Catalog-based access',
    text:
      'Many activities are currently available free of charge. Other entries may require registration, eligibility, institutional access, or payment. Current conditions are shown in each catalog entry.',
    cta: 'Explore skill development',
    path: serviceRoutesById['perceptual-skill-development'].path,
  },
  {
    id: 'institutional-programs',
    icon: 'graduationCap',
    title: serviceRoutesById['institutional-programs'].label,
    model: 'Configured institutional scope',
    text:
      'Course, cohort, assessment, analytics, educational research, and co-developed implementations are scoped according to the institution’s goals, participants, required capabilities, and support needs.',
    cta: 'Explore institutional programs',
    path: serviceRoutesById['institutional-programs'].path,
  },
  {
    id: 'perceptual-research-infrastructure',
    icon: 'research',
    title: serviceRoutesById['perceptual-research-infrastructure'].label,
    model: 'Custom study scope',
    text:
      'Pricing depends on the study protocol, samples, evaluator workflow, task configuration, blinding, monitoring, outputs, governance, and implementation support.',
    cta: 'Explore research infrastructure',
    path: serviceRoutesById['perceptual-research-infrastructure'].path,
  },
  {
    id: 'perceptual-data-annotation',
    icon: 'tags',
    title: serviceRoutesById['perceptual-data-annotation'].label,
    model: 'Custom dataset and evaluator scope',
    text:
      'Pricing depends on dataset size, perceptual constructs, evaluator requirements, number of judgments, repetitions, quality monitoring, outputs, governance, and coordination needs.',
    cta: 'Explore data annotation',
    path: serviceRoutesById['perceptual-data-annotation'].path,
  },
];

const scopeFactors = [
  {
    icon: 'grow',
    title: 'Scale',
    text:
      'Number of learners, cohorts, samples, evaluators, judgments, phases, or sites.',
  },
  {
    icon: 'sliders',
    title: 'Workflow complexity',
    text:
      'Tasks, scales, branching, assignments, blinding, repetitions, and monitoring requirements.',
  },
  {
    icon: 'team',
    title: 'Evaluator requirements',
    text:
      'Expertise, languages, preparation, coordination, panel structure, and repeated evaluations.',
  },
  {
    icon: 'customize',
    title: 'Customization',
    text:
      'Custom modules, protocols, collections, dashboards, exports, or technical development.',
  },
  {
    icon: 'dashboard',
    title: 'Outputs',
    text:
      'Data exports, reports, analytics, competency evidence, annotation summaries, or documentation.',
  },
  {
    icon: 'support',
    title: 'Support',
    text:
      'Onboarding, project coordination, monitoring, implementation support, or ongoing delivery.',
  },
  {
    icon: 'lock',
    title: 'Governance',
    text:
      'Consent, privacy, data access, retention, institutional responsibility, and ethical constraints.',
  },
  {
    icon: 'document',
    title: 'Rights and licensing',
    text:
      'Use of licensed materials, commercial use, reuse permissions, or institution-specific access conditions.',
  },
];

const startingPoints = [
  {
    title: 'I want to practise or explore activities',
    text: 'Start with the app catalog and review each entry’s current access conditions.',
    cta: 'Explore the catalog',
    path: catalogPath,
  },
  {
    title: 'I want to use All-Voiced in a course or institution',
    text: 'Review how Institutional Programs support configured educational implementation.',
    cta: 'Explore Institutional Programs',
    path: serviceRoutesById['institutional-programs'].path,
  },
  {
    title: 'I need the perceptual layer of a study',
    text: 'Explore study-centered perceptual workflow configuration and support.',
    cta: 'Explore Research Infrastructure',
    path: serviceRoutesById['perceptual-research-infrastructure'].path,
  },
  {
    title: 'I need structured judgments for a dataset',
    text: 'Review dataset-centered annotation workflows and evaluator coordination.',
    cta: 'Explore Data Annotation',
    path: serviceRoutesById['perceptual-data-annotation'].path,
  },
  {
    title: 'I am not sure',
    text: 'Contact All-Voiced with your goals, context, and constraints.',
    cta: 'Contact All-Voiced',
    path: '/contact',
  },
];

export default function AccessAndPricing() {
  return (
    <main className="service-landing-page access-and-pricing-page">
      <Helmet>
        <title>Access & Pricing | All-Voiced</title>
        <meta
          name="description"
          content="Understand how free and paid catalog entries, institutional implementations, research workflows, and perceptual data annotation projects are accessed and priced across All-Voiced."
        />
      </Helmet>

      <PageTracker pageName="AccessAndPricing" />

      <section className="service-landing-hero">
        <span className="services-eyebrow">
          Access & Pricing
        </span>

        <h1>
          Understand How All-Voiced Access and Pricing Work
        </h1>

        <p>
          All-Voiced combines free and paid catalog entries with configured
          institutional, research, and annotation services. Access and
          pricing depend on the type of resource, workflow, and
          implementation required.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to={catalogPath}
          >
            Explore the catalog
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/contact"
          >
            Contact All-Voiced
          </Link>
        </div>

        <div className="service-landing-panel">
          <span>Free Activities</span>
          <span className="service-landing-separator">•</span>
          <span>Paid Entries</span>
          <span className="service-landing-separator">•</span>
          <span>Institutional Access</span>
          <span className="service-landing-separator">•</span>
          <span>Custom Projects</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-intro">
        <span className="services-label">
          Access models
        </span>

        <h2>
          Different services use different access and pricing models
        </h2>

        <p>
          All-Voiced is not organized around one universal subscription or
          a single set of fixed packages. The appropriate model depends on
          whether someone is accessing a catalog entry, configuring an
          institutional implementation, running a study, or annotating a
          dataset.
        </p>

        <div className="service-landing-grid">
          {accessModelCards.map((card) => (
            <div className="service-landing-card" key={card.id}>
              <AVIcon icon={card.icon} size={28} color="#209385" />

              <h3>{card.title}</h3>

              <p className="services-micro">{card.model}</p>

              <p>{card.text}</p>

              <Link
                className="web-button web-button--secondary services-card-button"
                to={card.path}
              >
                {card.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Catalog access
        </span>

        <h2>
          Many activities can be explored without a custom project
        </h2>

        <p>
          Many Perceptual Skill Development activities are currently
          available free of charge and can be launched directly from the app
          catalog. Some entries may require registration, eligibility,
          institutional access, or another condition even when no payment is
          required.
        </p>

        <p>
          Each catalog entry explains its current access model, intended
          audience, and available launch pathway. Users do not need to
          contact All-Voiced before using ordinary self-service entries
          unless the entry says otherwise.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to={catalogPath}
          >
            Explore the catalog
          </Link>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Paid entries
        </span>

        <h2>
          Some specialized entries may require payment
        </h2>

        <p>
          Specialized activities, resources, Live Sessions, certifications,
          or licensed materials may use paid access models as the catalog
          evolves. Possible models may include a one-time purchase, paid
          entry or session, time-limited access, institutional access,
          licensed access, or another entry-specific model.
        </p>

        <p>
          The specific model, price, duration, eligibility, and included
          access will be shown in the relevant catalog entry when
          applicable. All-Voiced does not currently imply one universal
          subscription across the entire catalog.
        </p>
      </section>

      <section className="service-landing-section service-landing-highlight">
        <span className="services-label">
          Institutional Programs
        </span>

        <h2>
          Institutional implementations are configured, not selected from a
          fixed package
        </h2>

        <p>
          Institutional pricing may depend on the number and type of courses
          or cohorts, learner or evaluator groups, existing versus custom
          activities, program duration, analytics, reporting, assessment
          workflows, institutional access requirements, support, onboarding,
          co-development, and custom implementation.
        </p>

        <p>
          Some institutions may begin with existing free activities before
          deciding whether they need a configured Institutional Program.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>Institutional Programs</h3>

            <p>
              Explore configured educational implementation for courses,
              cohorts, assessment, analytics, and educational research.
            </p>
          </div>

          <Link
            className="web-button web-button--secondary"
            to="/meet"
          >
            Discuss an institutional program
          </Link>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Perceptual Research Infrastructure
        </span>

        <h2>
          Research workflows are scoped around the study
        </h2>

        <p>
          Research Infrastructure pricing may depend on the study protocol,
          number and type of samples, task and scale configuration,
          evaluator groups, repeated phases, blinding, multicenter or
          multilingual coordination, quality monitoring, dashboards,
          reports, exports, support during execution, governance
          requirements, and custom development.
        </p>

        <p>
          Pricing is based on the agreed perceptual workflow and
          implementation responsibilities, not simply on one participant or
          evaluator count.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--secondary"
            to="/meet"
          >
            Discuss a study
          </Link>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Perceptual Data Annotation
        </span>

        <h2>
          Annotation pricing depends on the dataset and evaluator workflow
        </h2>

        <p>
          Annotation pricing may depend on dataset size, sample duration or
          complexity where relevant, number of perceptual constructs, number
          of judgments per sample, evaluator profile, evaluator recruitment
          or panel coordination if included, familiarization, training or
          calibration where required, repeated items, multilingual
          annotation, quality and variability review, data dictionaries,
          reports, dashboards, exports, governance, permitted use, and
          custom technical implementation.
        </p>

        <p>
          There is no universal annotation price per file because the
          methodological design and evaluator requirements can differ
          substantially between projects.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--secondary"
            to="/meet"
          >
            Discuss a dataset
          </Link>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Scope factors
        </span>

        <h2>
          What shapes a custom proposal?
        </h2>

        <p>
          Custom pricing is not arbitrary. It reflects the concrete
          implementation factors needed for the agreed workflow. Not every
          factor applies to every project.
        </p>

        <div className="service-landing-grid four">
          {scopeFactors.map((factor) => (
            <div className="service-landing-card" key={factor.title}>
              <AVIcon icon={factor.icon} size={28} color="#209385" />

              <h3>{factor.title}</h3>

              <p>{factor.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Where to start
        </span>

        <h2>
          Choose the simplest appropriate pathway
        </h2>

        <p>
          The right starting point depends on whether you want to explore
          existing entries, configure an institution, run a study, annotate
          a dataset, or clarify the best path first.
        </p>

        <div className="service-landing-grid">
          {startingPoints.map((item) => (
            <div className="service-landing-card" key={item.title}>
              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <Link
                className="web-button web-button--secondary services-card-button"
                to={item.path}
              >
                {item.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <AccessAndPricingFAQ />

      <section className="services-final-cta">
        <h2>
          Find the right access model for your context
        </h2>

        <p>
          Explore current catalog entries or contact All-Voiced to discuss
          an institutional implementation, research workflow, or annotation
          project.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to={catalogPath}
          >
            Explore the catalog
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/contact"
          >
            Contact All-Voiced
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="AccessAndPricing" />
    </main>
  );
}
